<script setup lang="ts">
import type { Condition } from "../weather";

defineProps<{ condition: Condition; night?: boolean }>();
</script>

<template>
  <svg class="glyph" :class="`glyph--${condition}`" viewBox="0 0 64 64" aria-hidden="true">
    <template v-if="condition === 'clear' && !night">
      <circle class="sun" cx="32" cy="32" r="10.5" />
      <path
        class="rays"
        d="M32 11v6M32 47v6M11 32h6M47 32h6M17.15 17.15l4.25 4.25M42.6 42.6l4.25 4.25M17.15 46.85l4.25-4.25M42.6 21.4l4.25-4.25"
      />
    </template>

    <template v-else-if="condition === 'clear'">
      <path class="moon" d="M25.35 15.58A19 19 0 1 0 48.14 39.64A17 17 0 0 1 25.35 15.58Z" />
      <path class="star star--a" d="M45 10v7M41.5 13.5h7" />
      <path class="star star--b" d="M54.5 23.5v4.5M52.25 25.75h4.5" />
    </template>

    <template v-else-if="condition === 'cloudy'">
      <template v-if="!night">
        <circle class="sun" cx="42.5" cy="23" r="7.5" />
        <path
          class="rays rays--peek"
          d="M42.5 8.5v4M42.5 33.5v4M28 23h4M53 23h4M52.75 12.75l-2.83 2.83M32.25 12.75l2.83 2.83M52.75 33.25l-2.83-2.83M32.25 33.25l2.83-2.83"
        />
      </template>
      <path v-else class="moon" d="M41.1 10.67A10.5 10.5 0 1 0 53.05 24.05A9.2 9.2 0 0 1 41.1 10.67Z" />
      <path
        class="cloud"
        d="M19 48.5A7.5 7.5 0 0 1 18.05 33.56A10.5 10.5 0 0 1 38.82 30.58A9 9 0 1 1 40 48.5Z"
      />
    </template>

    <template v-else-if="condition === 'showers' || condition === 'rain' || condition === 'storm'">
      <path
        class="cloud"
        d="M21 39A8 8 0 0 1 19.56 23.13A11.5 11.5 0 0 1 42.34 20.07A9.5 9.5 0 1 1 43.5 39Z"
      />
      <g v-if="condition === 'showers'" class="rain">
        <path d="M24.5 45l-1.6 4.5" />
        <path d="M33 45l-1.6 4.5" />
        <path d="M41.5 45l-1.6 4.5" />
      </g>
      <g v-else-if="condition === 'rain'" class="rain rain--heavy">
        <path d="M22 44.5l-3.2 9" />
        <path d="M29.5 44.5l-3.2 9" />
        <path d="M37 44.5l-3.2 9" />
        <path d="M44.5 44.5l-3.2 9" />
      </g>
      <template v-else>
        <path class="bolt" d="M34.5 42.5l-6.5 9h6l-3.5 8.5" />
        <g class="rain">
          <path d="M22 45l-1.6 4.5" />
          <path d="M43.5 45l-1.6 4.5" />
        </g>
      </template>
    </template>

    <g v-else-if="condition === 'fog'" class="fog">
      <path d="M14 22h26M46 22h4" />
      <path d="M18 31h34" />
      <path d="M12 40h22M40 40h12" />
      <path d="M20 49h24" />
    </g>

    <template v-else>
      <circle class="pending" cx="32" cy="32" r="15" />
      <circle class="pending-dot" cx="32" cy="32" r="1.6" />
    </template>
  </svg>
</template>

<style scoped>
.glyph {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
  fill: none;
  stroke: currentColor;
  stroke-width: var(--glyph-stroke, 2px);
  stroke-linecap: round;
  stroke-linejoin: round;
}
.glyph :is(path, circle) { vector-effect: non-scaling-stroke; }

.sun { fill: color-mix(in srgb, var(--sun) var(--sun-fill, 34%), transparent); }
.moon { fill: color-mix(in srgb, var(--sun) var(--moon-fill, 16%), transparent); }
/* Opaque, so the cloud hides the sun or moon behind it. */
.cloud { fill: var(--surface); }
.rain { stroke: var(--accent); }
.bolt { stroke: var(--warn); }
.pending { stroke-dasharray: 3 6; opacity: 0.6; }
.pending-dot { fill: currentColor; stroke: none; }

@media (prefers-reduced-motion: no-preference) {
  .rays { transform-box: view-box; transform-origin: 32px 32px; animation: turn 60s linear infinite; }
  .rays--peek { transform-origin: 42.5px 23px; animation-duration: 80s; }
  .star { animation: twinkle 3.2s ease-in-out infinite alternate; }
  .star--b { animation-delay: -1.6s; }
  .rain path { animation: fall 1.15s linear infinite; }
  .rain path:nth-child(2) { animation-delay: -0.4s; }
  .rain path:nth-child(3) { animation-delay: -0.8s; }
  .rain path:nth-child(4) { animation-delay: -0.2s; }
  .rain--heavy path { animation-duration: 0.8s; }
  .bolt { animation: flash 4.5s steps(1, end) infinite; }
  .fog path { animation: drift 7s ease-in-out infinite alternate; }
  .fog path:nth-child(even) { animation-direction: alternate-reverse; animation-duration: 9s; }
  .pending { transform-box: view-box; transform-origin: 32px 32px; animation: turn 24s linear infinite; }
}

@keyframes turn { to { transform: rotate(360deg); } }
@keyframes twinkle { from { opacity: 0.25; } to { opacity: 1; } }
@keyframes drift { from { transform: translateX(-1.4px); } to { transform: translateX(1.4px); } }
@keyframes fall {
  0% { opacity: 0; transform: translate(0.7px, -2px); }
  25% { opacity: 1; }
  75% { opacity: 1; }
  100% { opacity: 0; transform: translate(-0.9px, 2.5px); }
}
@keyframes flash {
  0%, 6%, 10%, 100% { opacity: 1; }
  4%, 8% { opacity: 0.25; }
}
</style>
